import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';

export interface LightRaysProps extends React.HTMLAttributes<HTMLDivElement> {
  count?: number;
  color?: string;
  blur?: number;
  speed?: number;
  length?: string;
}

type LightRay = {
  id: string;
  left: number;
  rotate: number;
  width: number;
  swing: number;
  delay: number;
  duration: number;
  intensity: number;
};

function createRays(count: number, cycle: number): LightRay[] {
  if (count <= 0) return [];

  return Array.from({ length: count }, (_, index) => {
    const left = 8 + Math.random() * 84;
    const rotate = -28 + Math.random() * 56;
    const width = 160 + Math.random() * 160;
    const swing = 0.8 + Math.random() * 1.8;
    const delay = Math.random() * cycle;
    const duration = cycle * (0.75 + Math.random() * 0.5);
    const intensity = 0.6 + Math.random() * 0.5;

    return {
      id: `${index}-${Math.round(left * 10)}`,
      left,
      rotate,
      width,
      swing,
      delay,
      duration,
      intensity,
    };
  });
}

export function LightRays({
  className,
  style,
  count = 7,
  color = 'rgba(160, 210, 255, 0.2)',
  blur = 36,
  speed = 14,
  length = '70vh',
  ...props
}: LightRaysProps) {
  const cycleDuration = Math.max(speed, 0.1);
  const seed = useMemo(() => `${count}-${cycleDuration}`, [count, cycleDuration]);
  const [rays, setRays] = useState<LightRay[]>([]);

  useEffect(() => {
    setRays(createRays(count, cycleDuration));
  }, [seed, count, cycleDuration]);

  return (
    <div
      className={cn('pointer-events-none absolute inset-0 isolate overflow-hidden rounded-[inherit]', className)}
      style={
        {
          '--light-rays-color': color,
          '--light-rays-blur': `${blur}px`,
          '--light-rays-length': length,
          ...style,
        } as CSSProperties
      }
      {...props}
      aria-hidden="true"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 opacity-60"
          style={
            {
              background:
                'radial-gradient(circle at 20% 15%, color-mix(in srgb, var(--light-rays-color) 45%, transparent), transparent 70%)',
            } as CSSProperties
          }
        />
        <div
          className="absolute inset-0 opacity-60"
          style={
            {
              background:
                'radial-gradient(circle at 80% 10%, color-mix(in srgb, var(--light-rays-color) 35%, transparent), transparent 75%)',
            } as CSSProperties
          }
        />
        {rays.map((ray) => (
          <div
            key={ray.id}
            className="light-ray pointer-events-none absolute -top-[12%] left-[var(--ray-left)] h-[var(--light-rays-length)] w-[var(--ray-width)] origin-top -translate-x-1/2 rounded-full opacity-0 mix-blend-screen blur-[var(--light-rays-blur)]"
            style={
              {
                '--ray-left': `${ray.left}%`,
                '--ray-width': `${ray.width}px`,
                '--ray-rotate': `${ray.rotate}deg`,
                '--ray-swing': `${ray.swing}deg`,
                '--ray-intensity': ray.intensity,
                '--ray-duration': `${ray.duration}s`,
                '--ray-delay': `${ray.delay}s`,
              } as CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
}

